import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import PropTypes from 'prop-types';
import { useDrawer } from './DrawerContext';

/** @type {React.Context<DrawerNavigationContextValue|null>} */
const DrawerNavigationContext = createContext(null);

/**
 * DrawerNavigationProvider — owns drilldown navigation state
 * (activeDrillDownItem, triggerRef).
 *
 * Consumers use the `useDrawerNavigation()` hook instead of prop drilling.
 */
const DrawerNavigationProvider = ({ children }) => {
	const { isOpen } = useDrawer();

	// Which parent item is currently drilled into (null = top level)
	const [activeDrillDownItem, setActiveDrillDownItem] = useState(null);

	// Ref to the SubMenuTrigger button that opened the current submenu
	const triggerRef = useRef(null);

	// Reset drill-down on close
	useEffect(() => {
		if (!isOpen) {
			setActiveDrillDownItem(null);
			triggerRef.current = null;
		}
	}, [isOpen]);

	const onSubmenuOpen = useCallback((item, buttonRef) => {
		triggerRef.current = buttonRef?.current ?? null;
		setActiveDrillDownItem(item);
	}, []);

	const onSubmenuClose = useCallback(() => {
		setActiveDrillDownItem(null);
		// Do NOT null triggerRef here — it must persist until
		// the drilldown Modal's onExited fires (~200ms later).
	}, []);

	const value = useMemo(
		() => ({
			activeDrillDownItem,
			onSubmenuOpen,
			onSubmenuClose,
			triggerRef
		}),
		[activeDrillDownItem, onSubmenuOpen, onSubmenuClose]
		// triggerRef is a stable ref — intentionally excluded
	);

	return (
		<DrawerNavigationContext.Provider value={value}>
			{children}
		</DrawerNavigationContext.Provider>
	);
};

DrawerNavigationProvider.propTypes = {
	children: PropTypes.node.isRequired
};

/**
 * Returns the DrawerNavigationContext value.
 * Must be used inside DrawerNavigationProvider.
 *
 * @returns {DrawerNavigationContextValue}
 */
const useDrawerNavigation = () => {
	const context = useContext(DrawerNavigationContext);
	if (context === null) {
		throw new Error(
			'useDrawerNavigation must be used within a DrawerNavigationProvider'
		);
	}
	return context;
};

export {
	DrawerNavigationContext,
	DrawerNavigationProvider,
	useDrawerNavigation
};
